// client/src/pages/ApplicationsPortal.jsx
import React, { useState } from "react";
import Logo from "../../assets/RCF-PP.jpg";
import {
  Building2,
  GraduationCap,
  BookOpen,
  Bug,
  Search,
  Server,
  ExternalLink,
  Calendar,
  Monitor,
  Globe,
  ShieldCheck,
  Users // Added Users icon for the HR module
} from "lucide-react";

export default function ApplicationsPortal() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // Get today's formatted date
  const todayDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const applications = [
    {
      id: 1,
      title: "IMAS Development",
      description: "Development environment for testing and implementing new features.",
      category: "Development",
      status: "Development",
      version: "v2.0",
      technology: "React + Node + PostgreSQL",
      buttonText: "Open Development",
      color: "#3b82f6", // Blue
      bgColor: "#eff6ff",
      icon: Server,
      url: "http://localhost:3000/",
    },
    {
      id: 2,
      title: "IMAS Production",
      description: "Live production environment used by Rajalakshmi Children Foundation.",
      category: "Live",
      status: "Live",
      version: "v2.0",
      technology: "React + Node + PostgreSQL",
      buttonText: "Open Portal",
      color: "#10b981", // Green
      bgColor: "#ecfdf5",
      icon: GraduationCap,
      url: "https://imas.pratibhaposhak.in/",
    },
    {
      id: 3,
      title: "Library Management",
      description: "Digital library powered by Librarika.",
      category: "Live",
      status: "Live",
      version: "Cloud",
      technology: "Librarika",
      buttonText: "Open Library",
      color: "#f97316", // Orange
      bgColor: "#fff7ed",
      icon: BookOpen,
      url: "https://librarika.com/",
    },
    {
      id: 4,
      title: "Jira",
      description: "Project planning, sprint management and issue tracking.",
      category: "External",
      status: "Live",
      version: "Cloud",
      technology: "Atlassian",
      buttonText: "Open Jira",
      color: "#8b5cf6", // Purple
      bgColor: "#f5f3ff",
      icon: Bug,
      url: "https://www.atlassian.com/software/jira",
    },
    // New HR Module Added Below
    {
      id: 5,
      title: "HRMS Portal",
      description: "Human Resources Management System for staff attendance, payroll, and records.",
      category: "Live",
      status: "Live",
      version: "v1.0",
      technology: "React + Node",
      buttonText: "Open HRMS",
      color: "#0d9488", // Teal
      bgColor: "#ccfbf1",
      icon: Users,
      url: "https://hr.pratibhaposhak.in/", // Replace with your actual HR portal URL
    },
  ];

  const categories = ["All", "Development", "Live", "External"];

  const filteredApps = applications.filter((app) => {
    const matchesSearch =
      app.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      activeCategory === "All" || app.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="portal-container">
      {/* HEADER SECTION */}
      <header className="portal-header shadow-sm">
        <div className="header-left">
          <img src={Logo} alt="RCF Logo" className="header-logo" />
          <div>
            <h1 className="header-title">Rajalakshmi Children Foundation</h1>
            <p className="header-subtitle">Organization Applications Portal</p>
          </div>
        </div>
        <div className="header-right">
          <Calendar size={18} className="text-gray-500" />
          <span className="date-text">{todayDate}</span>
        </div>
      </header>

      {/* WELCOME BANNER */}
      <div className="welcome-banner shadow-sm">
        <div className="welcome-icon-wrapper">
          <Building2 size={28} color="#0369a1" />
        </div>
        <div className="welcome-text">
          <h2>Welcome to RCF Digital Workspace</h2>
          <p>
            Access all organizational applications from one place. Select the
            application you wish to use.
          </p>
        </div>
      </div>

      {/* STATISTICS SECTION */}
      <div className="stats-grid">
        <div className="stat-card shadow-sm">
          <div className="stat-icon-bg bg-blue">
            <Monitor size={22} color="#3b82f6" />
          </div>
          <div>
            <p className="stat-title">Applications</p>
            {/* Dynamically calculate total apps */}
            <p className="stat-value">{applications.length}</p>
          </div>
        </div>
        <div className="stat-card shadow-sm">
          <div className="stat-icon-bg bg-green">
            <Globe size={22} color="#10b981" />
          </div>
          <div>
            <p className="stat-title">Live</p>
            {/* Dynamically calculate Live apps */}
            <p className="stat-value">
              {applications.filter((app) => app.status === "Live").length}
            </p>
          </div>
        </div>
        <div className="stat-card shadow-sm">
          <div className="stat-icon-bg bg-orange">
            <Server size={22} color="#f97316" />
          </div>
          <div>
            <p className="stat-title">Development</p>
            {/* Dynamically calculate Development apps */}
            <p className="stat-value">
              {applications.filter((app) => app.status === "Development").length}
            </p>
          </div>
        </div>
        <div className="stat-card shadow-sm">
          <div className="stat-icon-bg bg-purple">
            <ShieldCheck size={22} color="#8b5cf6" />
          </div>
          <div>
            <p className="stat-title">Organization</p>
            <p className="stat-value">RCF</p>
          </div>
        </div>
      </div>

      {/* CONTROLS (SEARCH & FILTER) */}
      <div className="controls-section">
        <div className="search-wrapper shadow-sm">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search Applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-buttons">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-btn shadow-sm ${
                activeCategory === cat ? "active" : ""
              }`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* APPLICATIONS GRID */}
      <div className="apps-grid">
        {filteredApps.length > 0 ? (
          filteredApps.map((app) => {
            const IconComponent = app.icon;
            return (
              <div key={app.id} className="app-card shadow-md">
                <div className="app-card-content">
                  <div className="app-header">
                    <div
                      className="app-icon-box"
                      style={{
                        background: `linear-gradient(135deg, ${app.bgColor} 0%, white 100%)`,
                        border: `1px solid ${app.color}33`,
                      }}
                    >
                      <IconComponent size={32} color={app.color} />
                    </div>
                    <h3 className="app-title">{app.title}</h3>
                  </div>
                  <p className="app-description">{app.description}</p>
                  
                  <div className="divider"></div>
                  
                  <div className="app-badges">
                    <span
                      className="badge"
                      style={{
                        backgroundColor: app.status === "Live" ? "#ecfdf5" : "#eff6ff",
                        color: app.status === "Live" ? "#065f46" : "#1e40af",
                        border: `1px solid ${app.status === "Live" ? "#a7f3d0" : "#bfdbfe"}`
                      }}
                    >
                      <span className={`status-dot ${app.status === "Live" ? "bg-green-500" : "bg-blue-500"}`}></span>
                      {app.status}
                    </span>
                    <span className="badge version-badge">{app.version}</span>
                    <span className="badge tech-badge">{app.technology}</span>
                  </div>
                </div>

                <div className="app-card-footer">
                  <button
                    className="open-btn"
                    style={{
                      backgroundColor: app.color,
                      boxShadow: `0 4px 14px 0 ${app.color}40`,
                    }}
                    onClick={() => window.open(app.url, "_blank")}
                  >
                    <span>{app.buttonText}</span>
                    <ExternalLink size={16} className="btn-icon" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-results shadow-sm">
            <Search size={40} className="text-gray-300" />
            <h3>No applications found</h3>
            <p>Try adjusting your search or category filter.</p>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <footer className="portal-footer">
        <p>© 2026 Rajalakshmi Children Foundation</p>
        <p>Digital Applications Portal • Version 1.0</p>
      </footer>

      {/* INLINE STYLES */}
      <style>{`
        /* Global & Layout */
        .portal-container {
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          padding: 24px;
          height: 100vh; /* Changed from min-height: 100vh to fix scroll issue */
          overflow-y: auto; /* Force container to scroll vertically */
          background-color: #f8fafc;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          box-sizing: border-box;
        }
        
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        .shadow-md { box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
        
        /* Header */
        .portal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: #ffffff;
          padding: 20px 24px;
          border-radius: 12px;
          margin-bottom: 24px;
        }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-logo { width: 64px; height: 64px; border-radius: 8px; object-fit: cover; }
        .header-title { font-size: 24px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
        .header-subtitle { font-size: 14px; font-weight: 500; color: #64748b; }
        .header-right { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background-color: #f1f5f9; border-radius: 8px; }
        .date-text { font-size: 14px; font-weight: 600; color: #475569; }

        /* Welcome Banner */
        .welcome-banner {
          display: flex;
          align-items: center;
          gap: 20px;
          background-color: #e0f2fe;
          padding: 24px 32px;
          border-radius: 12px;
          margin-bottom: 24px;
          border: 1px solid #bae6fd;
        }
        .welcome-icon-wrapper { background-color: #ffffff; padding: 16px; border-radius: 50%; box-shadow: 0 4px 6px -1px rgba(2, 132, 199, 0.1); }
        .welcome-text h2 { font-size: 22px; font-weight: 700; color: #075985; margin-bottom: 6px; }
        .welcome-text p { font-size: 15px; color: #0369a1; }

        /* Stats Section */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }
        .stat-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background-color: #ffffff;
          padding: 20px;
          border-radius: 12px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid #f1f5f9;
        }
        .stat-card:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
        .stat-icon-bg { padding: 12px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .bg-blue { background-color: #eff6ff; }
        .bg-green { background-color: #ecfdf5; }
        .bg-orange { background-color: #fff7ed; }
        .bg-purple { background-color: #f5f3ff; }
        .stat-title { font-size: 13px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
        .stat-value { font-size: 24px; font-weight: 700; color: #0f172a; margin-top: 4px; }

        /* Controls */
        .controls-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 20px;
        }
        .search-wrapper {
          position: relative;
          flex-grow: 1;
          max-width: 500px;
        }
        .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .search-input {
          width: 100%;
          padding: 14px 20px 14px 48px;
          font-size: 15px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background-color: #ffffff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
        
        .filter-buttons { display: flex; gap: 10px; }
        .filter-btn {
          padding: 10px 20px;
          border: 1px solid #e2e8f0;
          background-color: #ffffff;
          color: #64748b;
          font-size: 14px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .filter-btn:hover { background-color: #f8fafc; color: #0f172a; border-color: #cbd5e1; }
        .filter-btn.active { background-color: #1e293b; color: #ffffff; border-color: #1e293b; }

        /* Apps Grid */
        .apps-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }
        
        .app-card {
          display: flex;
          flex-direction: column;
          background-color: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #f1f5f9;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .app-card:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border-color: #e2e8f0;
        }
        
        .app-card-content { padding: 24px; flex-grow: 1; }
        
        .app-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 16px; }
        .app-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          transition: transform 0.3s ease;
        }
        .app-card:hover .app-icon-box { transform: scale(1.05) rotate(-2deg); }
        .app-title { font-size: 18px; font-weight: 700; color: #0f172a; line-height: 1.3; }
        
        .app-description { font-size: 14px; color: #475569; line-height: 1.5; min-height: 42px; margin-bottom: 16px; }
        
        .divider { height: 1px; background-color: #f1f5f9; margin: 16px 0; width: 100%; }
        
        .app-badges { display: flex; flex-wrap: wrap; gap: 8px; }
        .badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        .status-dot { width: 6px; height: 6px; border-radius: 50%; margin-right: 6px; }
        .bg-green-500 { background-color: #10b981; }
        .bg-blue-500 { background-color: #3b82f6; }
        
        .version-badge { background-color: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
        .tech-badge { background-color: #f8fafc; color: #64748b; border: 1px dashed #cbd5e1; }
        
        .app-card-footer { padding: 20px 24px; background-color: #f8fafc; border-top: 1px solid #f1f5f9; }
        
        .open-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 0;
          color: #ffffff;
          font-size: 15px;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .open-btn:hover { filter: brightness(1.1); transform: scale(1.02); }
        .open-btn:active { transform: scale(0.98); }
        .btn-icon { transition: transform 0.3s ease; }
        .open-btn:hover .btn-icon { transform: translate(3px, -3px); }

        .no-results {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          background-color: #ffffff;
          border-radius: 12px;
          border: 1px dashed #cbd5e1;
          text-align: center;
        }
        .no-results h3 { font-size: 18px; color: #0f172a; margin: 16px 0 8px 0; }
        .no-results p { color: #64748b; font-size: 15px; }

        /* Footer */
        .portal-footer {
          text-align: center;
          padding: 32px 0 16px 0;
          border-top: 1px solid #e2e8f0;
          color: #94a3b8;
          font-size: 14px;
        }
        .portal-footer p { margin-bottom: 4px; }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .apps-grid { grid-template-columns: repeat(3, 1fr); }
        }

        @media (max-width: 992px) {
          .apps-grid { grid-template-columns: repeat(2, 1fr); }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .controls-section { flex-direction: column; align-items: stretch; }
          .search-wrapper { max-width: 100%; }
        }

        @media (max-width: 768px) {
          .portal-header { flex-direction: column; align-items: flex-start; gap: 16px; }
          .header-right { width: 100%; justify-content: center; }
          .welcome-banner { flex-direction: column; text-align: center; }
          .filter-buttons { flex-wrap: wrap; }
          .filter-btn { flex-grow: 1; text-align: center; }
        }

        @media (max-width: 576px) {
          .apps-grid { grid-template-columns: 1fr; }
          .stats-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}